<template>
  <div class="default-layout">
    <div class="default-layout__notification-container">
      <ClientOnly>
        <SNotificationBar id="topPromoBar" />
      </ClientOnly>
    </div>

    <div class="default-layout__header-container">
      <SHeader />
    </div>

    <main class="default-layout__main">
      <slot />
    </main>

    <ClientOnly>
      <ProductListingFiltersModal @selected-city="onSelectedCity" />
      <OfflineBadge />
      <Notification />
    </ClientOnly>

    <div class="default-layout__footer-container">
      <FooterV2 />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useThemeCategoryStore } from '@/stores/themeCategory';
import { useUiStore } from '@/stores/ui';
import { useProductFilterStore } from '@/stores/productFilter';
import { storeToRefs } from 'pinia';
import { onMounted, watch } from 'vue';
import { useNuxtApp } from '#imports';

const OfflineBadge = defineAsyncComponent(() => import('@/components/atoms/OfflineBadge.vue'));
const SNotificationBar = defineAsyncComponent(
  () => import('@/components/molecules/notifications/SNotificationBar.vue')
);

const SHeader = defineAsyncComponent(() => import('@/components/Header/SHeader.vue'));
const FooterV2 = defineAsyncComponent(() => import('@/components/organisms/FooterV2.vue'));
const ProductListingFiltersModal = defineAsyncComponent(
  () => import('@/components/Pages/ProductListing/ProductListingFiltersModal.vue')
);
const Notification = defineAsyncComponent(() => import('@/components/molecules/notifications/Notification.vue'));

const themeCategoryStore = useThemeCategoryStore();
const { $grid } = useNuxtApp();
const uiStore = useUiStore();
const productFilterStore = useProductFilterStore();

function onSelectedCity(city: string) {
  productFilterStore.loadFilters(city);
}

onMounted(async () => {
  await themeCategoryStore.fetchThemeCategories();
});

watch(
  () => uiStore.state.isCartSidebarOpen,
  (val) => {
    if (process.client && !$grid.md) {
      document.body.style.overflow = val ? 'hidden' : 'visible';
    }
  }
);
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.default-layout {
  /* Ensure the layout maintains a consistent structure */
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  /* Reserve space for header */
  &__header-container {
    min-height: 60px; /* Adjust based on actual header height */

    @include bp(md) {
      min-height: 80px; /* Adjust for desktop */
    }
  }

  /* Ensure main content area grows to fill available space */
  &__main {
    flex: 1;
    min-height: 100vh;
  }

  &__footer {
    margin-bottom: 100px;

    @include bp(md) {
      margin-bottom: 64px;
    }
  }
}
</style>